import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaUserCog }    from '@react-icons/all-files/fa/FaUserCog';
import { FaUsers }      from '@react-icons/all-files/fa/FaUsers';
import { FaCog }        from '@react-icons/all-files/fa/FaCog';
import { FaHome }       from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageForm, { FormField } from '../../../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../../manager/usuario.reducer';

const BACK = '/configuracion/usuarios/usuarios';

interface UsuarioForm {
  id:         string;
  usuario:    string;
  nombre:     string;
  apellido:   string;
  nombreCompleto: string;
  email:      string;
  habilitado: string;
  telefonoMovil1: string;
  telefonoMovil2: string;
  esContador: boolean;
  contadorT: string;
  sysPerfil: any
}

const empty: UsuarioForm = {
  id: '', usuario: '', nombre: '', apellido: '', nombreCompleto: '', email: '', habilitado: 'true',
  telefonoMovil1: '', telefonoMovil2: '', esContador: false, contadorT: '', sysPerfil: null,
};

const fields: FormField<UsuarioForm>[] = [
  {
    name:     'id',
    label:    'ID',
    readOnly: true,
    editOnly: true,
  },
  {
    name:        'usuario',
    label:       'Usuario',
    required:    true,
    placeholder: 'Nombre de usuario',
  },
  {
    name:        'nombre',
    label:       'Nombre',
    required:    true,
    placeholder: 'Nombre',
  },
  {
    name:        'apellido',
    label:       'Apellido',
    required:    true,
    placeholder: 'Apellido',
  },
  {
    name:        'nombreCompleto',
    label:       'Nombre Completo',
    required:    true,
    placeholder: 'Nombre Completo',
  },
  {
    name:        'email',
    label:       'Email',
    type:        'email',
    required:    true,
    placeholder: 'correo@ejemplo.com',
  },
  {
    name:        'telefonoMovil1',
    label:       'Telefono Movil 1',
    required:    false,
    placeholder: 'Número de Teléfono Móvil 1',
  },
  {
    name:  'habilitado',
    label: 'Habilitado',
    type:  'checkbox',
  },
];

export default function UsuarioFormPage() {
  const { id }   = useParams<{ id: string }>();
  const isNew    = !id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity        = useAppSelector(s => s.usuario.entity) as any;
  const updating      = useAppSelector(s => s.usuario.updating);
  const updateSuccess = useAppSelector(s => s.usuario.updateSuccess);

  const [form, setForm]     = useState<UsuarioForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof UsuarioForm, string>>>({});

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id!));
    } else {
      dispatch(reset());
      setForm(empty);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNew && entity?.id) {
      setForm({
        id:         String(entity.id),
        usuario:    entity.usuario    ?? '',
        nombre:     entity.nombre     ?? '',
        apellido:   entity.apellido   ?? '',
        nombreCompleto: entity.nombreCompleto ?? '',
        email:      entity.email      ?? '',
        habilitado: String(entity.habilitado ?? true),
        telefonoMovil1: entity.telefonoMovil1 ?? '',
        telefonoMovil2: entity.telefonoMovil2 ?? '',
        esContador: entity.esContador ?? false,
        contadorT: entity.contadorT ?? '',
        sysPerfil: entity.sysPerfil ?? null,
      });
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, {
        state: {
          successMsg: isNew
            ? 'Usuario creado correctamente.'
            : 'Usuario actualizado correctamente.',
        },
      });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldChange = (name: keyof UsuarioForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Partial<Record<keyof UsuarioForm, string>> = {};
    if (!form.usuario.trim())  errs.usuario  = 'El usuario es requerido';
    if (!form.nombre.trim())   errs.nombre   = 'El nombre es requerido';
    if (!form.apellido.trim()) errs.apellido = 'El apellido es requerido';
    if (!form.nombreCompleto.trim()) errs.nombreCompleto = 'El nombre completo es requerido';
    if (!form.email.trim())    errs.email    = 'El email es requerido';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = {
      ...(isNew ? {} : { id: Number(form.id) }),
      usuario:    form.usuario,
      nombre:     form.nombre,
      apellido:   form.apellido,
      nombreCompleto: form.nombreCompleto,
      email:      form.email,
      habilitado: form.habilitado === 'true',
      telefonoMovil1: form.telefonoMovil1,
      telefonoMovil2: form.telefonoMovil2,
      esContador: form.esContador,
      contadorT: form.contadorT,
      sysPerfil: form.sysPerfil ? { id: Number(form.sysPerfil) } : null,
    };

    if (isNew) {
      dispatch(createEntity(payload));
    } else {
      dispatch(updateEntity(payload));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio',        to: '/',                          icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion',             icon: <FaCog /> },
        { label: 'Usuarios',      to: '/configuracion/usuarios',    icon: <FaUsers /> },
        { label: 'Usuarios',      to: BACK,                         icon: <FaUserCog /> },
        { label: isNew ? 'Nuevo Usuario' : `Editar Usuario #${id}` },
      ]} />

      <PageHeader
        icon={<FaUserCog />}
        iconColor="#0ea5e9"
        title={isNew ? 'Nuevo Usuario' : 'Editar Usuario'}
        subtitle={isNew
          ? 'Completa los campos para crear un nuevo usuario'
          : `Modificando usuario #${id}`}
      />

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={isNew}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(BACK)}
        accentColor="#0ea5e9"
      />
    </div>
  );
}
